import React from "react";
import { FormikErrors, FormikProps, withFormik } from "formik";
import {
  AccordionIcon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { Button } from "@chakra-ui/button";
import { Box, Divider, HStack, VStack } from "@chakra-ui/layout";
import { AdminApiClient } from "../api/adminApiClient";
import { FormStack } from "../components/FormStack/FormStack";
import { CloudServiceEbetApi } from "../api/ebetApiModels";
import { SwitchField } from "../components/fields/SwitchField/SwitchField";
import { TextField } from "../components/fields/TextField/TextField";
import { IntegerField } from "../components/fields/IntegerField/IntegerField";
import { useAdminApiRequest } from "../hooks/useApiRequest";
import { AdminApiEnumConfigurations } from "../enums/AdminApiEnumConfigurations";
import { ChakraReactSelectField } from "../components/fields/SelectField/ChakraReactSelectField";
import { TimePeriod } from "../components/RegistrationFormConfiguration/RegistrationFormTimePeriod";
import { EbetJoinMethodList } from "../enums/EbetJoinMethod";
import { EbetStringFormattingMethodList } from "../enums/EbetStringFormattingMethod";
import { AddRemoveBoxField } from "../components/fields/BoxField/AddRemoveBoxField";
import { FutureDateCalculationMethodList } from "../enums/FutureDateCalculationMethod";
import { AddRemoveAccordionField } from "../components/fields/AccordionField/AddRemoveAccordionField";

const emptyStrToNull = (value: string | null): string | null => {
  if (!value || value.trim() === "") {
    return null;
  } else {
    return value;
  }
};

interface IntegrationEMDISConfigurationFormValues
  extends Omit<CloudServiceEbetApi.EMDISConfiguration, "MembershipManagement"> {
  MembershipManagement?: MembershipManagement;
}

interface MembershipManagement
  extends Omit<
    CloudServiceEbetApi.MembershipManagement,
    "SupportedMembershipRates" | "MembershipRenewalSettings"
  > {
  SupportedMembershipRates?: {
    Key: string;
    Value: CloudServiceEbetApi.MembershipRate;
  }[];
  MembershipRenewalSettings?: MembershipRenewalSettings;
}

interface MembershipRenewalSettings
  extends Omit<
    CloudServiceEbetApi.MembershipRenewalSettings,
    "ProRatedRateTypeMapping"
  > {
  ProRatedRateTypeMapping?: {
    Key: string;
    Value: string | null;
  }[];
}

interface IntegrationEMDISConfigurationFormProps {
  apiClient: AdminApiClient;
  businessId: string;
  integrationId: string;
  configuration: { [key: string]: unknown } | null | undefined;
  isDisabled?: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  onFailure?: (message?: string) => void;
}

const InnerForm = ({
  businessId,
  integrationId,
  isSubmitting,
  submitForm,
  setFieldValue,
  isValid,
  onCancel,
  values,
}: IntegrationEMDISConfigurationFormProps &
  FormikProps<IntegrationEMDISConfigurationFormValues>) => {
  const { data: timeZoneOptions, isLoading: timeZoneOptionsLoading } =
    useAdminApiRequest(
      (apiClient) =>
        apiClient.getEnumConfigurations(AdminApiEnumConfigurations.TimeZone),
      []
    );

  const { data: ratesOptions, isLoading: ratesLoading } = useAdminApiRequest(
    (apiClient) =>
      apiClient.getIntegrationMMSList(businessId, integrationId, "Rates"),
    []
  );

  return (
    <FormStack>
      <VStack w="100%" maxW="1200px" alignItems="flex-start" pb={10}>
        <SwitchField
          horizontal
          label="Log Http Requests"
          name="LogHttpRequests"
        />

        <TextField horizontal label="Connection Url" name="ConnectionUrl" />

        <IntegerField
          horizontal
          label="Session Keep Alive Divisor"
          name="SessionKeepAliveDivisor"
        />

        <TextField horizontal label="Vendor Code" name="VendorCode" />

        <TextField horizontal label="Device Code" name="DeviceCode" />

        <TextField horizontal label="Private Key" name="PrivateKey" />

        <IntegerField horizontal label="Venue Id" name="VenueId" />

        <TextField horizontal label="Target System" name="TargetSystem" />

        <IntegerField
          horizontal
          label="Time Drift Tolerance Minutes"
          name="TimeDriftToleranceMinutes"
        />

        <IntegerField
          horizontal
          label="Idle Session Timeout"
          name="IdleSessionTimeout"
        />

        <Box
          w="100%"
          p={4}
          border="1px solid"
          borderColor="cherryButtonDark.50"
          borderRadius="md"
        >
          <Text fontSize="sm">
            <b>Data Export Settings</b> :
          </Text>
          <Divider mb={2} />

          <HStack w="100%" spacing={4} alignItems="flex-start">
            <IntegerField
              horizontal
              flex={1}
              label="Max Get Data Row Count"
              name="DataExport.MaxGetDataRowCount"
            />

            <IntegerField
              horizontal
              flex={1}
              label="ARC Data Export API Version"
              name="DataExport.ARCDataExportAPIVersion"
            />
          </HStack>
        </Box>

        <IntegerField
          horizontal
          label="Timeout Seconds"
          name="TimeoutSeconds"
        />

        <ChakraReactSelectField
          horizontal
          label="Time Zone Id"
          name="TimeZoneId"
          selectedOptionColor="blue"
          isLoading={timeZoneOptionsLoading}
          options={
            timeZoneOptions?.map((timeZone) => {
              return {
                label: timeZone.Value!,
                value: timeZone.Key!,
              };
            }) || []
          }
        />

        <TextField
          horizontal
          label="Key Vault Service Account Uid"
          name="KeyVaultServiceAccountUid"
        />
      </VStack>

      <Tabs variant="edit" w="100%" isLazy>
        <TabList>
          <HStack spacing={3}>
            <Tab>Membership Management</Tab>

            <Tab>Membership Renewal Settings</Tab>
          </HStack>
        </TabList>

        <TabPanels>
          <TabPanel>
            <VStack w="100%" alignItems="flex-start">
              <TextField
                horizontal
                label="ARC Membership Management API Version"
                name="MembershipManagement.ARCMembershipManagementAPIVersion"
              />

              <TextField
                horizontal
                label="New Member Default Card Number"
                name="MembershipManagement.NewMemberDefaultCardNumber"
              />

              <TextField
                horizontal
                label="Club Prefix Digits"
                name="MembershipManagement.ClubPrefixDigits"
              />

              <TextField
                horizontal
                label="Card Number Format"
                name="MembershipManagement.CardNumberFormat"
              />

              <ChakraReactSelectField
                horizontal
                allowEmpty
                label="New Member Join Method"
                name="MembershipManagement.NewMemberJoinMethod"
                selectedOptionColor="blue"
                options={EbetJoinMethodList()}
              />

              <SwitchField
                horizontal
                label="Replicate Home Address To Postal Address"
                name="MembershipManagement.ReplicateHomeAddressToPostalAddress"
              />

              <SwitchField
                horizontal
                label="Add As Preliminary Member"
                name="MembershipManagement.AddAsPreliminaryMember"
              />

              <ChakraReactSelectField
                horizontal
                allowEmpty
                label="New Member Name String Formatting Method"
                name="MembershipManagement.NewMemberNameStringFormattingMethod"
                selectedOptionColor="blue"
                options={EbetStringFormattingMethodList()}
              />

              <SwitchField
                horizontal
                label="Abort Before Creating Member"
                name="MembershipManagement.AbortBeforeCreatingMember"
              />

              <AddRemoveAccordionField
                label="Membership Rate"
                fieldsName="MembershipManagement.SupportedMembershipRates"
                fields={values.MembershipManagement?.SupportedMembershipRates}
                newField={{ Key: "", Value: {} }}
                setFieldValue={setFieldValue}
                accordionButton={(
                  label,
                  fieldsName,
                  fields,
                  field,
                  fieldIdx,
                  DeleteButton
                ) => (
                  <>
                    <Box flexShrink={1}>
                      <b>
                        {emptyStrToNull(field.Key) ?? `Rate ${fieldIdx + 1}`}
                      </b>
                      &nbsp; - {`#${field.Value.RateNo ?? fieldIdx + 1}`}
                    </Box>
                    <Box flexGrow={1}>
                      <DeleteButton
                        idx={fieldIdx}
                        aria-label={`Delete ${label}.`}
                      />
                    </Box>
                    <AccordionIcon />
                  </>
                )}
              >
                {(label, fieldsName, fields, field, fieldIdx, DeleteButton) => (
                  <VStack w="100%" alignItems="flex-start">
                    <ChakraReactSelectField
                      horizontal
                      label="Key"
                      name={`${fieldsName}[${fieldIdx}].Key`}
                      selectedOptionColor="blue"
                      isLoading={ratesLoading}
                      options={
                        ratesOptions?.map((rate: any) => {
                          return {
                            label: rate.RateDescription!,
                            value: rate.Type!,
                          };
                        }) || []
                      }
                    />

                    <IntegerField
                      horizontal
                      label="Rate No"
                      name={`${fieldsName}[${fieldIdx}].Value.RateNo`}
                    />

                    <IntegerField
                      horizontal
                      label="Pay New Member With Fee Id"
                      name={`${fieldsName}[${fieldIdx}].Value.PayNewMemberWithFeeId`}
                    />

                    <IntegerField
                      horizontal
                      label="Joining Fee"
                      name={`${fieldsName}[${fieldIdx}].Value.JoiningFee`}
                    />

                    <TimePeriod
                      headerLabel="Membership Duration Time Period"
                      fieldName={`${fieldsName}[${fieldIdx}].Value.MembershipDurationTimePeriod`}
                      timePeriod={field?.Value.MembershipDurationTimePeriod}
                      isRelativeListbox={true}
                    />

                    <ChakraReactSelectField
                      horizontal
                      allowEmpty
                      label="Expiry Date Calculation Method"
                      name={`${fieldsName}[${fieldIdx}].Value.ExpiryDateCalculationMethod`}
                      selectedOptionColor="blue"
                      options={FutureDateCalculationMethodList()}
                    />

                    <Box
                      w="100%"
                      p={4}
                      border="1px solid"
                      borderColor="cherryButtonDark.50"
                      borderRadius="md"
                    >
                      <Text fontSize="sm">
                        <b>
                          Roll To Calendar Year Expiry Date Calculation
                          Configuration
                        </b>
                      </Text>
                      <Divider mb={2} />

                      <VStack w="100%" alignItems="flex-start">
                        <TextField
                          horizontal
                          label="Start From Next Calendar Year After Date"
                          name={`${fieldsName}[${fieldIdx}].Value.RollToCalendarYearExpiryDateCalculationConfiguration.StartFromNextCalendarYearAfterDate`}
                        />

                        <TimePeriod
                          headerLabel="Adjust End Date By Time Period"
                          fieldName={`${fieldsName}[${fieldIdx}].Value.RollToCalendarYearExpiryDateCalculationConfiguration.AdjustEndDateByTimePeriod`}
                          timePeriod={
                            field?.Value
                              ?.RollToCalendarYearExpiryDateCalculationConfiguration
                              ?.AdjustEndDateByTimePeriod
                          }
                          isRelativeListbox={true}
                          borderColor="cherryButtonDark.100"
                        />
                      </VStack>
                    </Box>

                    <Box
                      w="100%"
                      p={4}
                      border="1px solid"
                      borderColor="cherryButtonDark.50"
                      borderRadius="md"
                    >
                      <Text fontSize="sm">
                        <b>
                          Roll To Custom Future Date Calculation Configuration
                        </b>
                      </Text>
                      <Divider mb={2} />

                      <VStack w="100%" alignItems="flex-start">
                        <TextField
                          horizontal
                          label="Start From Next Calendar Year After Date"
                          name={`${fieldsName}[${fieldIdx}].Value.RollToCustomFutureDateCalculationConfiguration.CalculateFromDate`}
                        />

                        <TextField
                          horizontal
                          label="Roll On Or After Date"
                          name={`${fieldsName}[${fieldIdx}].Value.RollToCustomFutureDateCalculationConfiguration.RollOnOrAfterDate`}
                        />

                        <IntegerField
                          horizontal
                          label="On Or After Date Year Shift"
                          name={`${fieldsName}[${fieldIdx}].Value.RollToCustomFutureDateCalculationConfiguration.OnOrAfterDateYearShift`}
                        />

                        <IntegerField
                          horizontal
                          label="Before Date Year Shift Fee"
                          name={`${fieldsName}[${fieldIdx}].Value.RollToCustomFutureDateCalculationConfiguration.BeforeDateYearShift`}
                        />

                        <TimePeriod
                          headerLabel="Adjust End Date By Time Period"
                          fieldName={`${fieldsName}[${fieldIdx}].Value.RollToCustomFutureDateCalculationConfiguration.AdjustEndDateByTimePeriod`}
                          timePeriod={
                            field?.Value
                              ?.RollToCustomFutureDateCalculationConfiguration
                              ?.AdjustEndDateByTimePeriod
                          }
                          isRelativeListbox={true}
                          borderColor="cherryButtonDark.100"
                        />
                      </VStack>
                    </Box>
                  </VStack>
                )}
              </AddRemoveAccordionField>
            </VStack>
          </TabPanel>

          <TabPanel>
            <VStack w="100%" alignItems="flex-start">
              <SwitchField
                horizontal
                label="Allow Rate Change"
                name="MembershipManagement.MembershipRenewalSettings.AllowRateChange"
              />

              <SwitchField
                horizontal
                label="Calculate Amount Payable Using Rate"
                name="MembershipManagement.MembershipRenewalSettings.CalculateAmountPayableUsingRate"
              />

              <SwitchField
                horizontal
                label="Restrict Rates To Associated Rate Type Group"
                name="MembershipManagement.MembershipRenewalSettings.RestrictRatesToAssociatedRateTypeGroup"
              />

              <TextField
                horizontal
                label="Currency Code"
                name="MembershipManagement.MembershipRenewalSettings.CurrencyCode"
              />

              <AddRemoveBoxField
                headerLabel="Renewal Rate Type Groups"
                fieldsName="MembershipManagement.MembershipRenewalSettings.RenewalRateTypeGroups"
                fields={
                  values.MembershipManagement?.MembershipRenewalSettings
                    ?.RenewalRateTypeGroups
                }
                newField={[""]}
                setFieldValue={setFieldValue}
              >
                {(fiedsName, fields, field, fieldIdx, DeleteButton) => (
                  <HStack
                    w="100%"
                    alignItems="flex-start"
                    key={fieldIdx}
                    spacing={2}
                  >
                    <ChakraReactSelectField
                      horizontal
                      isMulti
                      label={`Renewal Rate Type Groups ${fieldIdx + 1}`}
                      name={`${fiedsName}[${fieldIdx}]`}
                      selectedOptionColor="blue"
                      isLoading={ratesLoading}
                      options={
                        ratesOptions?.map((rate: any) => {
                          return {
                            label: rate.RateDescription!,
                            value: rate.Type!,
                          };
                        }) || []
                      }
                    />

                    {(fields?.length ?? 0) > 0 && (
                      <DeleteButton idx={fieldIdx} />
                    )}
                  </HStack>
                )}
              </AddRemoveBoxField>

              <TextField
                horizontal
                label="Fee Descriptor"
                name="MembershipManagement.MembershipRenewalSettings.FeeDescriptor"
              />

              <TextField
                horizontal
                label="Payment Descriptor"
                name="MembershipManagement.MembershipRenewalSettings.PaymentDescriptor"
              />

              <TextField
                horizontal
                label="Tax Code"
                name="MembershipManagement.MembershipRenewalSettings.TaxCode"
              />

              <TimePeriod
                headerLabel="Renewal Allowed After Expiry Period"
                fieldName="MembershipManagement.MembershipRenewalSettings.RenewalAllowedAfterExpiryPeriod"
                timePeriod={
                  values.MembershipManagement?.MembershipRenewalSettings
                    ?.RenewalAllowedAfterExpiryPeriod
                }
                isRelativeListbox={true}
              />

              <TimePeriod
                headerLabel="Renewal Allowed Before Expiry Period"
                fieldName="MembershipManagement.MembershipRenewalSettings.RenewalAllowedBeforeExpiryPeriod"
                timePeriod={
                  values.MembershipManagement?.MembershipRenewalSettings
                    ?.RenewalAllowedBeforeExpiryPeriod
                }
                isRelativeListbox={true}
              />

              <AddRemoveBoxField
                headerLabel="Pro Rated Rate Type Mapping"
                fieldsName="MembershipManagement.MembershipRenewalSettings.ProRatedRateTypeMapping"
                fields={
                  values.MembershipManagement?.MembershipRenewalSettings
                    ?.ProRatedRateTypeMapping
                }
                newField={{ Key: "", Value: "" }}
                setFieldValue={setFieldValue}
              >
                {(fiedsName, fields, field, fieldIdx, DeleteButton) => (
                  <HStack
                    w="100%"
                    alignItems="flex-start"
                    key={fieldIdx}
                    spacing={2}
                  >
                    <ChakraReactSelectField
                      horizontal
                      label={`Pro Rated Rate Type Mapping Key`}
                      name={`${fiedsName}[${fieldIdx}].Key`}
                      selectedOptionColor="blue"
                      isLoading={ratesLoading}
                      options={
                        ratesOptions?.map((rate: any) => {
                          return {
                            label: rate.RateDescription!,
                            value: rate.Type!,
                          };
                        }) || []
                      }
                    />
                    <ChakraReactSelectField
                      horizontal
                      label={`Pro Rated Rate Type Mapping Value`}
                      name={`${fiedsName}[${fieldIdx}].Value`}
                      selectedOptionColor="blue"
                      isLoading={ratesLoading}
                      options={
                        ratesOptions?.map((rate: any) => {
                          return {
                            label: rate.RateDescription!,
                            value: rate.Type!,
                          };
                        }) || []
                      }
                    />
                    {(fields?.length ?? 0) > 0 && (
                      <DeleteButton idx={fieldIdx} />
                    )}
                  </HStack>
                )}
              </AddRemoveBoxField>
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <HStack width="100%" justifyContent="start" spacing="3" pt="4">
        <Button
          colorScheme="cherryButton"
          type="submit"
          isLoading={isSubmitting}
          disabled={isSubmitting || !isValid}
          onClick={submitForm}
        >
          Save
        </Button>
        <Button disabled={isSubmitting} onClick={onCancel}>
          Cancel
        </Button>
      </HStack>
    </FormStack>
  );
};

export const IntegrationEMDISConfigurationForm = withFormik<
  IntegrationEMDISConfigurationFormProps,
  IntegrationEMDISConfigurationFormValues
>({
  mapPropsToValues: ({ configuration }) => {
    const emdisConf = configuration as CloudServiceEbetApi.EMDISConfiguration;
    return {
      ...emdisConf,
      MembershipManagement:
        (emdisConf.MembershipManagement && {
          ...emdisConf.MembershipManagement,
          SupportedMembershipRates:
            (emdisConf.MembershipManagement.SupportedMembershipRates &&
              Object.entries(
                emdisConf.MembershipManagement.SupportedMembershipRates
              ).map(([key, value]) => {
                return {
                  Key: key,
                  Value: value,
                };
              })) ||
            undefined,
          MembershipRenewalSettings:
            (emdisConf.MembershipManagement.MembershipRenewalSettings && {
              ...emdisConf.MembershipManagement.MembershipRenewalSettings,
              ProRatedRateTypeMapping:
                (emdisConf.MembershipManagement.MembershipRenewalSettings
                  .ProRatedRateTypeMapping &&
                  Object.entries(
                    emdisConf.MembershipManagement.MembershipRenewalSettings
                      .ProRatedRateTypeMapping
                  ).map(([key, value]) => {
                    return {
                      Key: key,
                      Value: value,
                    };
                  })) ||
                undefined,
            }) ||
            undefined,
        }) ||
        undefined,
    };
  },
  handleSubmit: async (values, { props }) => {
    let configurationUpdate: CloudServiceEbetApi.EMDISConfiguration = {
      ...values,
      MembershipManagement:
        (values.MembershipManagement && {
          ...values.MembershipManagement,
          SupportedMembershipRates: {},
          MembershipRenewalSettings:
            (values.MembershipManagement.MembershipRenewalSettings && {
              ...values.MembershipManagement.MembershipRenewalSettings,
              ProRatedRateTypeMapping: {},
            }) ||
            undefined,
        }) ||
        undefined,
    };

    if (values.MembershipManagement?.SupportedMembershipRates) {
      values.MembershipManagement.SupportedMembershipRates.map((rate) => {
        configurationUpdate.MembershipManagement!.SupportedMembershipRates![
          rate.Key
        ] = rate.Value;
      });
    }

    if (
      values.MembershipManagement?.MembershipRenewalSettings
        ?.ProRatedRateTypeMapping
    ) {
      values.MembershipManagement.MembershipRenewalSettings.ProRatedRateTypeMapping.map(
        (rate) => {
          configurationUpdate.MembershipManagement!.MembershipRenewalSettings!.ProRatedRateTypeMapping![
            rate.Key
          ] = rate.Value;
        }
      );
    }

    let result = await props.apiClient.updateIntegrationConfiguration(
      props.businessId,
      props.integrationId,
      // `updateIntegrationConfiguration` is typed as accepting a JSON string,
      // but (as in IntegrationConfigurationForm) the runtime client actually
      // PUTs whatever value it is given as the request body.
      configurationUpdate as any
    );

    if (result.ok) {
      if (props.onSuccess) {
        props.onSuccess();
      }
    } else {
      if (props.onFailure) {
        props.onFailure(result.message);
      }
    }
  },

  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: true,
  validate: (values) => {
    let errors: FormikErrors<IntegrationEMDISConfigurationFormValues> = {};

    return errors;
  },
})(InnerForm);
